// apps/api/src/modules/poi/poi-enrichment.service.ts
import { exec } from 'child_process'
import { promisify } from 'util'
import OpenAI from 'openai'
import { createLogger } from '@/common/utils/logger'
import { db } from '@/core/database/firebase'
import { geohash } from '@my-roadtrip/utils'
import path from 'path'

const execAsync = promisify(exec)
const logger = createLogger('poi-enrichment-service')

// --- OpenAI ---
const openai = new OpenAI({ apiKey: process.env.OPENAI_API_KEY })

// --- CONFIGURATION ---
// IMPORTANT: Paths must be absolute for exec to work correctly
const OSMIUM_PBF_PATH = process.env.OSMIUM_PBF_PATH || path.resolve(process.cwd(), 'europe-roadtrip.osm.pbf'); // Default to Europe PBF if env not set
if (!OSMIUM_PBF_PATH) {
  logger.error('Environment variable OSMIUM_PBF_PATH is not set. This is critical for the POI enrichment service.')
  throw new Error('OSMIUM_PBF_PATH environment variable is missing.')
}
// Ensure the path is absolute for exec to work consistently
const ABSOLUTE_OSMIUM_PBF_PATH = path.resolve(process.cwd(), OSMIUM_PBF_PATH);


// Semantic Categories and Helpers - Copied from scripts/seed-smart-pipeline.mjs

const SEMANTIC_CATEGORIES = { /* ... copied from seed-smart-pipeline.mjs ... */
  NATURE: ['natural', 'landuse=forest', 'place=island', 'waterway'],
  CULTURE: ['historic', 'tourism=museum', 'amenity=place_of_worship', 'amenity=theatre', 'amenity=cinema'],
  SIGHTSEEING: ['tourism=viewpoint', 'tourism=attraction', 'tourism=artwork', 'mountain_pass', 'bridge'],
  ACTIVITY: ['leisure', 'sport', 'highway=path'],
  ADVENTURE: ['tourism=alpine_hut', 'tourism=wilderness_hut', 'aerialway'], 
  ENGINEERING: ['man_made', 'waterway=dam'],
  HOSPITALITY: ['tourism=hotel', 'tourism=camp_site', 'amenity=restaurant', 'amenity=bar', 'amenity=cafe'],
  LOCAL_PROD: ['craft'],
  MARITIME: ['leisure=marina', 'man_made=pier', 'harbour'],
  UNIQUE_SHOP: ['shop=antiques', 'amenity=marketplace'],
  HISTORIC_FUN: ['railway=funicular', 'railway=preserved', 'leisure=casino'],
  WELLNESS: ['leisure=sauna', 'leisure=water_park', 'sport=swimming'], 
  INFO: ['tourism=information'],
  TRANSPORT: [],
  ESSENTIALS: ['amenity=fuel']
}

function getSemanticCategory(tags: Record<string, any>): string {
  if (tags.railway) {
    if (tags.railway === 'funicular' || tags.railway === 'preserved' || tags.railway === 'narrow_gauge') return 'HISTORIC_FUN'
    return 'TRANSPORT'
  }
  if (tags.aeroway || tags.public_transport) return 'TRANSPORT'
  
  if (tags.tourism === 'alpine_hut' || tags.tourism === 'wilderness_hut') return 'ADVENTURE'
  if (tags.aerialway) return 'ADVENTURE'
  if (tags.man_made || tags.waterway === 'dam') {
    if (tags.man_made === 'pier') return 'MARITIME'
    return 'ENGINEERING'
  }
  
  if (tags.leisure === 'sauna' || tags.leisure === 'water_park' || tags.leisure === 'beach_resort') return 'WELLNESS'
  if (tags.leisure === 'marina' || tags.harbour || tags.amenity === 'ferry_terminal' || tags.historic === 'wreck') return 'MARITIME'
  if (tags.shop === 'antiques' || tags.amenity === 'marketplace') return 'UNIQUE_SHOP'
  if (tags.leisure === 'casino') return 'HISTORIC_FUN'
  if (tags.tourism === 'information') return 'INFO'
  if (tags.amenity === 'fuel') return 'ESSENTIALS'
  
  if (tags.natural || tags.landuse === 'forest') return 'NATURE'
  if (tags.historic || tags.tourism === 'museum' || tags.amenity === 'theatre' || tags.amenity === 'cinema') return 'CULTURE'
  if (tags.craft) return 'LOCAL_PROD'
  
  if (tags.tourism === 'hotel' || tags.tourism === 'camp_site' || tags.tourism === 'guest_house') return 'HOSPITALITY'
  if (tags.amenity === 'restaurant' || tags.amenity === 'cafe' || tags.amenity === 'bar') return 'HOSPITALITY'
  
  if (tags.leisure || tags.sport || tags.tourism === 'zoo' || tags.tourism === 'theme_park') return 'ACTIVITY'
  if (tags.tourism === 'viewpoint' || tags.tourism === 'attraction' || tags.mountain_pass || tags.bridge) return 'SIGHTSEEING'
  
  return 'UNKNOWN'
}

function preFilterCandidate(candidate: Record<string, any>): boolean {
  const tags = candidate.tags || {}
  const cat = candidate.semanticCategory
  const name = (candidate.name || '').toLowerCase()

  if (!name) return false

  if (['NATURE', 'CULTURE', 'SIGHTSEEING', 'ACTIVITY', 'ESSENTIALS', 'LOCAL_PROD', 'ADVENTURE', 'ENGINEERING', 'MARITIME', 'HISTORIC_FUN', 'UNIQUE_SHOP', 'WELLNESS', 'INFO'].includes(cat)) {
    if (cat === 'UNIQUE_SHOP') {
       const shopBlacklist = ['cash', 'express', 'troc', 'occaz', 'converter', 'lidl', 'action']
       if (shopBlacklist.some(c => name.includes(c))) return false
    }
    return true 
  }

  if (cat === 'HOSPITALITY') {
    const blacklist = [
      'mcdonald', 'burger king', 'kfc', 'subway', 'ibis', 'novotel', 'mercure', 'formule 1', 'starbucks', 'costa coffee',
      'carrefour', 'leclerc', 'intermarche', 'auchan', 'lidl', 'aldi', 'super u', 'monoprix', 'casino'
    ]
    if (blacklist.some(c => name.includes(c))) return false
    if (tags.brand) return false
    if (tags.stars && parseInt(tags.stars) < 4) return false
    
    const badCuisine = ['burger', 'kebab', 'sandwich', 'fast_food', 'pizza', 'tacos'] 
    if (tags.cuisine && badCuisine.some(c => tags.cuisine.includes(c))) return false
    
    return true
  }
  return true
}

// Helper to get score without AI
function getLiteScore(poi: Record<string, any>): number {
    let score = 5 
    const cat = poi.semanticCategory
    
    if (cat === 'NATURE' || cat === 'SIGHTSEEING') score = 7
    if (cat === 'CULTURE') score = 6
    if (cat === 'ADVENTURE' || cat === 'ENGINEERING') score = 8 
    if (cat === 'HOSPITALITY') score = 4 
    if (cat === 'UNIQUE_SHOP') score = 6
    if (cat === 'HISTORIC_FUN') score = 7
    
    return score
}


export class POIEnrichmentService {
  /**
   * Fetches raw OSM data for a given OSM ID from the local PBF file.
   * @param osmId The OSM ID of the node.
   * @returns Raw OSM tags if found, null otherwise.
   */
  async getRawOsmData(osmId: string): Promise<any | null> {
    logger.info(`Fetching raw OSM data for ID: ${osmId} from ${ABSOLUTE_OSMIUM_PBF_PATH}`)
    try {
      // osmium show -f json -t node <pbf_file> n<osmId> outputs a GeoJSON FeatureCollection
      // for the specified node.
      const { stdout } = await execAsync(`osmium show -f json -t node ${ABSOLUTE_OSMIUM_PBF_PATH} n${osmId}`);
      const data = JSON.parse(stdout);

      if (data.type === 'FeatureCollection' && data.features && data.features.length > 0) {
        const feature = data.features[0];
        if (feature.geometry && feature.geometry.type === 'Point' && feature.properties) {
            const tags = feature.properties; // osmium show -f json puts tags directly in properties
            const coords = feature.geometry.coordinates; // [lng, lat]
            return {
                id: osmId,
                lat: coords[1],
                lng: coords[0],
                tags: tags,
                semanticCategory: getSemanticCategory(tags),
                name: tags.name // Ensure name is extracted
            }
        }
      }
      return null;
    } catch (error) {
      logger.error(`Error fetching raw OSM data for ${osmId}:`, error as any)
      return null
    }
  }

  /**
   * Enriches a POI with AI (description, score, etc.)
   * @param rawPOI The raw POI data from OSM.
   * @returns Enriched POI data.
   */
  async enrichPOIWithAI(rawPOI: Record<string, any>): Promise<any> {
    const poiLabel = String(rawPOI.name || rawPOI.id || 'unknown')
    logger.info(`Enriching POI with AI: ${poiLabel}`)
    const prompt = `
    Describe this place for a Road Trip app (max 30 words):
    Name: ${rawPOI.name || 'Unknown'}
    Tags: ${JSON.stringify(rawPOI.tags)}
    Category: ${rawPOI.semanticCategory}
    
    Also give a 'roadTripScore' (0-10) based on tourism value.
    
    CRITICAL SCORING RULES:
    - Nature/Views/Monuments: Score liberally (good view = 7+).
    - Hotels/Restaurants/Cafes: BE EXTREMELY STRICT. Score < 5 by default.
      - ONLY give > 7 if it is WORLD FAMOUS, HISTORIC (e.g. "Cafe de Flore", "Ritz"), or UNIQUE EXPERIENCE.
      - Standard local restaurants = 3/10.
      - Fast food/Chains = 0/10.
    - Gas Stations: Score 0/10 unless it is ICONIC/SCENIC (e.g. Route 66 vintage station).
    - Shops/Markets: Score > 6 only if Historic/Artisan/Antique.
    - Casinos: Score > 7 only if Historic Architecture (e.g. Belle Epoque).
    
    Return JSON: { "description": string, "score": number }
    `
    
    try {
      const response = await openai.chat.completions.create({
        model: process.env.OPENAI_MODEL || 'gpt-4o-mini',
        messages: [{ role: 'user', content: prompt }],
        response_format: { type: 'json_object' }
      })
      const messageContent = response.choices[0].message.content
      if (!messageContent) {
        throw new Error('Empty response from OpenAI')
      }
      const result = JSON.parse(messageContent)
      return { ...result, aiEnriched: true }
    } catch (error) {
      logger.error(`AI enrichment failed for ${String(rawPOI.name || rawPOI.id || 'unknown')}:`, error as any)
      return { description: '', score: getLiteScore(rawPOI), aiEnriched: false } // Fallback to lite score
    }
  }

  /**
   * Gets POI details, enriching with AI if not already in Firestore.
   * @param osmId The OSM ID of the POI.
   * @returns Enriched POI details.
   */
  async getEnrichedPoiDetails(osmId: string): Promise<any | null> {
    const poiRef = db.collection('poi_database').doc(`osm-${osmId}`)
    const doc = await poiRef.get()

    if (doc.exists && doc.data()?.aiEnriched) {
      logger.info(`Returning cached enriched POI details for ${osmId}`)
      return doc.data()
    }

    logger.info(`Fetching and enriching POI ${osmId} from local PBF`)
    const rawOsmData = await this.getRawOsmData(osmId)

    if (!rawOsmData) {
      logger.warn(`OSM data not found for ID: ${osmId}`)
      return null
    }

    const enrichment = await this.enrichPOIWithAI(rawOsmData)

    // Format for legacy compliance (like in seed-local-pbf.mjs)
    const tagsMap = rawOsmData.tags || {}
    const website = tagsMap.website || tagsMap['contact:website'] || (null as string | null)
    const phone = tagsMap.phone || tagsMap['contact:phone'] || (null as string | null)
    const openingHours = tagsMap.opening_hours || (null as string | null)
    const cuisine = tagsMap.cuisine || (null as string | null)
    const postcode = tagsMap['addr:postcode'] || (null as string | null)
    const city = tagsMap['addr:city'] || (null as string | null)
    const street = tagsMap['addr:street'] || (null as string | null)
    const brand = tagsMap.brand || (null as string | null)

    const finalPOI = {
      name: rawOsmData.name || `POI-${osmId}`,
      category: rawOsmData.semanticCategory.toLowerCase(),
      lat: rawOsmData.lat,
      lng: rawOsmData.lng,
      description: enrichment.description,
      roadTripScore: enrichment.score,
      confidence: 80, // High confidence since it's enriched
      uniqueness: enrichment.score * 10,
      verified: true,
      source: 'openstreetmap',
      coverImage: `https://source.unsplash.com/800x600/?${rawOsmData.semanticCategory.toLowerCase()}`,
      website,
      phone,
      openingHours,
      cuisine,
      postcode,
      city,
      street,
      brand,
      geohash4: geohash(rawOsmData.lat, rawOsmData.lng, 4),
      geohash5: geohash(rawOsmData.lat, rawOsmData.lng, 5),
      geohash6: geohash(rawOsmData.lat, rawOsmData.lng, 6),
      geohash7: geohash(rawOsmData.lat, rawOsmData.lng, 7),
      geohash8: geohash(rawOsmData.lat, rawOsmData.lng, 8),
      tags: Object.keys(tagsMap),
      metadata: {
        needsAiEnrichment: !enrichment.aiEnriched,
        importedAt: new Date().toISOString(),
        osmId: rawOsmData.id,
        rawTags: tagsMap
      },
      aiEnriched: enrichment.aiEnriched,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    }

    // Save to Firestore for future requests
    await poiRef.set(finalPOI, { merge: true })
    logger.info(`Enriched POI ${osmId} saved to Firestore.`)

    return finalPOI
  }

  /**
   * Filters POIs by category and relevance.
   * This is a utility function that can be used for "Smart Filtering" on demand.
   * @param pois Array of POI objects.
   * @param filters Criteria to filter by (e.g., categories, minScore).
   */
  filterAndSortPOIs(pois: any[], filters: { categories?: string[], minScore?: number }): any[] {
    let filtered = pois;

    if (filters.categories && filters.categories.length > 0) {
      const allowedCategories = new Set(filters.categories.map(c => c.toLowerCase()));
      filtered = filtered.filter(poi => allowedCategories.has(poi.category.toLowerCase()));
    }

    if (filters.minScore !== undefined) {
      filtered = filtered.filter(poi => poi.roadTripScore >= filters.minScore!);
    }

    // Default sorting by score, then name
    filtered.sort((a, b) => (b.roadTripScore || 0) - (a.roadTripScore || 0) || a.name.localeCompare(b.name));

    return filtered;
  }
}
